import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import MobileHeader from "src/app/components/datatable/header/MobileHeader";
import TableHeader from "src/app/components/datatable/header/TableHeader";
import MobileUserActions from "src/app/components/datatable/miscellaneous/MobileUserActions";
import Pagination from "src/app/components/datatable/pagination/Pagination";
import { Icon } from "src/app/components/icon/Icon";
import Loader from "src/app/components/loader/Loader";
import DisableModal from "src/app/components/modal/DisableModal";
import { ModalContent } from "src/app/components/modal/types";
import Search from "src/app/components/search/Search";
import projectCSS from "src/app/modules/user-projects/Projects.module.css";
import iconOptions from "src/assets/iconsGraphics/optionDots.svg";
import { ROUTES } from "src/config/constants";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { sortFieldGenerator } from "src/core/helpers/global/global";
import { AccountResponse } from "src/core/models/auth";
import { Column } from "src/core/models/global";
import { AccountsService } from "src/core/services/auth";
import { TableHead } from "src/types/global";

const headers: Array<TableHead> = [
  {
    name: "Ime korisnika",
    field: "firstName",
    sortable: true,
    class: "u-w20 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Email",
    field: "email",
    sortable: true,
    class: "u-w15 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Telefon",
    field: "phoneNumber",
    sortable: true,
    class: "u-w15 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Ciljna skupina",
    field: "companyName",
    sortable: true,
    class: "u-w15 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  { name: "", field: "", sortable: false, class: "u-w35 u-t-align-end u-t-uppercase u-t-secondary u-t-f16p", mclass: "u-mb20px u-t-f16p u-t-lh15" },
];

function Active() {
  const [users, setUsers] = useState<Array<AccountResponse>>([]);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(6);
  const [numberOfPages, setNumberOfPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sorting, setSorting] = useState<Column>({ name: "firstName", sortDescending: false });
  const [loading, setLoading] = useState(true);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disableModalContent, setDisableModalContent] = useState<ModalContent>();
  const [headerMenuActive, setHeaderMenuActive] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AccountResponse>();
  const [searchTerm, setSearchTerm] = useState("");

  const headerMenuRef = useRef<Array<any>>([]);
  const setHeaderMenuRef = (key: number, ref: any) => {
    headerMenuRef.current[key] = ref;
  };
  const [actionsMenuActive, setActionsMenuActive] = useState(false);

  const actionsMenuRef = useRef<Array<any>>([]);
  const setActionsMenuRef = (key: number, ref: any) => {
    actionsMenuRef.current[key] = ref;
  };
  const modalData = useContext(ModalContext);

  useEffect(() => {
    if (loading) {
      const timeoutID = setTimeout(() => {
        const sort = sortFieldGenerator(sorting.name ?? "", sorting.sortDescending ?? false);
        AccountsService.getActive(recordsPerPage, searchTerm, currentPage, sort)
          .then((response) => {
            if (response?.records) {
              setUsers(response.records);
              setRecordsPerPage(response.pageSize ?? 0);
              setCurrentPage(response.pageNum ?? 1);
              setNumberOfPages(response.totalNumOfPages ?? 0);
            }
          })
          .catch((e: ApiResult) => {
            if (!modalData?.content?.contentSet) {
              modalData?.triggerError(e);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      }, 500);
      return () => clearTimeout(timeoutID);
    }
  }, [loading, searchTerm, currentPage, sorting, recordsPerPage, modalData]);

  useEffect(() => {
    setLoading(true);
  }, [currentPage, sorting, searchTerm]);

  function handleDisableModal(userId: number | undefined) {
    const userForBlocking = users.find((user) => user.id === userId);
    setDisableModalContent({
      entityId: userId,
      header: "Blokiraj korisnika",
      text: "Jeste li sigurni da želite blokirati korisnika",
      highlightedText: `${userForBlocking?.firstName} ${userForBlocking?.lastName}`,
    });
    if (actionsMenuActive) {
      setActionsMenuActive(false);
    }
    setDisableModalOpen(true);
  }

  function disableUser(userId: number | undefined, message: string) {
    if (userId) {
      setLoading(true);
      AccountsService.disable(userId, message)
        .then((response: any) => {
          if (response) {
            const newUsers = users.filter((user) => user.id !== userId);
            setUsers(newUsers);
            modalData?.triggerSuccess(response);
          }
        })
        .catch((e: ApiResult) => {
          modalData?.triggerError(e);
        })
        .finally(() => {
          setLoading(false);
          setDisableModalOpen(false);
        });
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!headerMenuRef.current[0]?.contains(event.target as Node) && headerMenuActive) {
        setHeaderMenuActive(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
  }, [headerMenuRef, headerMenuActive]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!actionsMenuRef.current[0]?.contains(event.target as Node) && actionsMenuActive) {
        setActionsMenuActive(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
  }, [actionsMenuRef, actionsMenuActive]);

  function downloadExcel() {
    setActionsMenuActive(false);
    setLoading(true);
    AccountsService.getExcel()
      .then((response: any) => {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Korisnici.xlsx");
        document.body.appendChild(link);
        link.click();
      })
      .catch((e: ApiResult) => {
        modalData?.triggerError(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <div className="u-pos-relative">
      {/* Mobile users HTML */}
      <div className={classNames("u-pt10px", "u-bg-background", projectCSS.usersContainer)}>
        {headerMenuActive && (
          <MobileHeader
            setRef={setHeaderMenuRef}
            defaultSort={sorting}
            headers={headers.filter((head) => head.sortable)}
            onSorting={(column: Column) => {
              setSorting(column);
              setHeaderMenuActive(false);
            }}
          />
        )}
        {actionsMenuActive && (
          <MobileUserActions
            user={selectedUser}
            handleUser={(userId: number | undefined) => {
              handleDisableModal(userId);
              setActionsMenuActive(false);
            }}
            setRef={setActionsMenuRef}
          />
        )}
        <div className="u-flex u-flex-content-center">
          <h1 className="u-t-primary u-t-uppercase">Korisnici</h1>
        </div>
        <div className="u-flex u-flex-content-end u-mt20px u-mb20px">
          <Link to={ROUTES.adminUsersBlocked} className="u-t-primary-dark u-t-underline u-t-f16p u-t-lh15 u-t-w400 u-cursor-pointer">
            Pregledaj blokirane korisnike
          </Link>
        </div>

        {users.length && !loading && (
          <div
            onClick={() => setHeaderMenuActive(true)}
            className={classNames("u-flex u-flex-items-center u-mb20px u-mt10px u-col-xs-12 u-col-sm-12 u-cursor-pointer", projectCSS.mobileFilterCont)}
          >
            <label className="u-t-secondary u-mr10px u-t-f18p u-t-lh15">Poredaj po:</label>
            <span className="u-mr10px u-t-f18p u-t-lh15 u-t-lowercase">{headers.find((x) => x.field === sorting.name)?.name}</span>
            <Icon name={sorting.sortDescending ? "arrow-down" : "arrow-up"} className="u-t-f18p u-t-primary" />
          </div>
        )}
        <ul>
          {!loading &&
            users.map((account: AccountResponse) => (
              <ListItem
                key={account.id}
                user={account}
                openActions={() => {
                  setSelectedUser(account);
                  setActionsMenuActive(true);
                }}
              />
            ))}
        </ul>
      </div>
      {/* Desktop users HTML */}
      <div className={classNames("u-box", "u-flex-wrap", "u-t-align-center", projectCSS.tableContainer)}>
        <div>
          <h1 className="u-t-primary u-t-uppercase">Korisnici</h1>
        </div>
        <div className="u-flex u-flex-content-between u-flex-items-center">
          <div className="u-flex u-flex-items-center">
            <Search handleSearchTerm={setSearchTerm}></Search>
            <button
              onClick={downloadExcel}
              type="button"
              className={classNames("u-z-index1", "u-t-f16p", "u-pl30px", "u-cursor-pointer", projectCSS.exportBtn)}
            >
              Preuzmi
              <Icon name="download" className="u-t-valign-mid u-pl10px" />
            </button>
          </div>
          <Link
            to={ROUTES.adminUsersBlocked}
            className={classNames("u-t-uppercase", "u-t-nodecoration", "u-t-w400", "u-cursor-pointer", projectCSS.viewBlockedBtn)}
          >
            Pregledaj blokirane korisnike
          </Link>
        </div>
        <div className={classNames(projectCSS.tableWrap)}>
          <table className="u-w100">
            <TableHeader defaultSort={sorting} headers={headers} onSorting={setSorting} />
            <tbody>
              {!users.length && !loading && (
                <tr className="u-col-12 u-t-halign-c">
                  <td colSpan={5}>Nema korisnika</td>
                </tr>
              )}
              {!loading &&
                users.map((account: AccountResponse) => (
                  <tr className="u-col-12" key={account.id}>
                    <td className="u-w20 u-t-align-start u-t-f16p">{`${account.firstName} ${account.lastName}`}</td>
                    <td className="u-w15 u-t-align-start u-t-f16p">{account.email}</td>
                    <td className="u-w15 u-t-align-start u-t-f16p">{account.phoneNumber}</td>
                    <td className="u-w15 u-t-align-start u-t-f16p">{account.targetGroup}</td>
                    <td className="u-w35 u-t-align-end u-t-f16p">
                      <Link
                        to={ROUTES.showAdminUserProjects(account.id)}
                        className={classNames("u-t-nodecoration", "u-t-w400", "u-cursor-pointer", projectCSS.blueFillBtn)}
                      >
                        Poslani upiti
                      </Link>
                      <button
                        className={classNames("u-cursor-pointer", projectCSS.whiteFillBtn, projectCSS.actionBtn)}
                        onClick={() => handleDisableModal(account.id)}
                      >
                        Blokiraj korisnika
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      {loading && <Loader />}
      <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} numberOfPages={numberOfPages} onPageChange={setCurrentPage} />
      {disableModalOpen && disableModalContent && (
        <DisableModal close={(isOpen: boolean) => setDisableModalOpen(isOpen)} disableUser={disableUser} modalContent={disableModalContent} />
      )}
    </div>
  );
}

export default Active;

const ListItem = ({ user, openActions }: { user: AccountResponse; openActions: () => void }) => {
  return (
    <li>
      <div className={classNames("u-bg-white", "u-flex", "u-flex-column", "u-mb5per", projectCSS.userProjects)}>
        {user.targetGroup !== null ? (
          <div>
            <div className="u-flex u-col-12">
              <div className="u-flex u-flex-content-between u-col-11">
                <span className="u-t-secondary u-mr5px">Tvrtka:</span>
                <span className="u-col-xs-5 u-t-align-end">{user.targetGroup}</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openActions();
                }}
                className="u-col-1 u-flex u-flex-content-end u-cursor-pointer"
              >
                <img alt="option button" src={iconOptions}></img>
              </button>
            </div>
            <div className="u-col-12 u-flex -u-flex-content-between u-mt5px u-mb5px">
              <h5 className="u-m0 u-t-w400 u-t-f18p u-t-lh15 u-m0">{`${user.firstName} ${user.lastName}`}</h5>
            </div>
          </div>
        ) : (
          <div className="u-flex u-col-12">
            <div className="u-flex u-flex-content-start u-col-11">
              <h5 className="u-m0 u-t-w400 u-t-f18p u-t-lh15 u-m0">{`${user.firstName} ${user.lastName}`}</h5>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openActions();
              }}
              className="u-col-1 u-flex u-flex-content-end u-cursor-pointer"
            >
              <img alt="option button" src={iconOptions}></img>
            </button>
          </div>
        )}
        <div className="u-flex u-flex-content-start u-col-11">
          <span className="u-col-6 u-t-align-start u-word-break-all">{user.email}</span>
          <span className="u-col-6 u-mlauto u-t-align-end">{user.phoneNumber}</span>
        </div>
      </div>
    </li>
  );
};
